import { HttpClient } from '@angular/common/http';
import { Injectable, isSignal } from '@angular/core';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { NF_response } from '../interface/NF-response';
import { InvoiceItem } from '../interface/invoice-item';
import { ApiMovementPost, MovementGet, IStockMoveError } from '../interface/movement';
import { Operation } from '../interface/operation';
import { Part } from '../interface/part';
import { MoveTypes } from '../shared/lists/move-type';
import { environment } from '../../environments/environment';
import { firstValueFrom, Observable, of } from 'rxjs';
import { DataService } from './data.service';
import { MkgOSPart } from '../class/mkg-os-part';
import { OrderItem } from '../component/page/company/order/model/order-item';
import { OrderFull } from '../component/page/company/order/model/order.model';
import { MkgOS } from '../class/mkg-os';
import { PartService } from './part.service';
import { OperationService } from './operation.service';
import { OperationFunction } from '../shared/lists/operation-functions';
import { IOrderItem } from '../component/page/company/order/interface/order-item.interface';
import { IOrder } from '../component/page/company/order/interface/order.interface';
import { ApiPaginatedMovementQueryParams, ApiPaginatedResponse, PaginationService } from './pagination.service';
import { API_ERRORS } from '../shared/lists/errors';
import { IssueOsInvoiceResponse } from '@app/component/page/company/os/services/invoice-os.service';
import { IssueOrderInvoiceResponse } from '@app/component/page/company/order/service/issue-order-invoice.service';

@Injectable({
  providedIn: 'root'
})
export class MovementService {
  private _isFromMatrix = false;

  constructor(
    private http: HttpClient,
    private dataService: DataService,
    private _partService: PartService,
    private _operationService: OperationService
  ) { }

  async getAll(): Promise<MovementGet[]> {
    const url = `${environment.mkgoURL}/api/v1/stockMove`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    let resp = await this.http.get(url, header).pipe(first()).toPromise();
    return resp as MovementGet[];
  }

  async getById(id: string): Promise<MovementGet> {
    const url = `${environment.mkgoURL}/api/v1/stockMove/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp = await this.http.get(url, header).pipe(first()).toPromise()
    return resp as MovementGet;
  }

  async getByItemId(id: string): Promise<MovementGet[]> {
    const url = `${environment.mkgoURL}/api/v1/stockMove/item/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    let resp = await firstValueFrom(this.http.get(url, header).pipe(first()));
    return (resp as MovementGet[]).map(mov => ({
      ...mov,
      createdAt: new Date(mov.createdAt),
    }));
  }

  /**
   * Every persisted stock movement of one invoice (`note` holds the invoice id),
   * scoped by the invoice-owning company.
   *
   * This is the evidence used to classify how a source invoice actually changed
   * physical stock before reversing it in a refund. Pass `cnpj` when the invoice
   * belongs to another unit of the group so the query runs in the same scope
   * used at emission time. An empty array means the invoice has no persisted
   * movement evidence.
   */
  async getByNoteId(noteId: string, cnpj?: string): Promise<MovementGet[]> {
    const url = `${environment.mkgoURL}/api/v1/stockMove/note/${noteId}`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj ?? this._isFromMatrix));
    const resp = await firstValueFrom(this.http.get<MovementGet[]>(url, header).pipe(first()));
    return (resp || []).map(mov => ({
      ...mov,
      createdAt: mov.createdAt ? new Date(mov.createdAt) : mov.createdAt,
    }));
  }

  async getAllPartsByPeriodOfTime(startDate: string, endDate: string): Promise<Part[]> {
    const url = `${environment.mkgoURL}/api/v1/stockMove/find-all/parts?startDate=${startDate}&endDate=${endDate}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const response = await firstValueFrom(this.http.get(url, header).pipe(first(), map(res => (res as any).parts)));
    return response as any;
  }

  async register(movement: ApiMovementPost, cnpj?: string) {
    const url = `${environment.mkgoURL}/api/v1/stockMove`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj ?? this._isFromMatrix));
    const resp = await this.http.post(url, movement, header).pipe(first()).toPromise();
    return resp
  }

  async rebuildOrderOsAvailableStock(partId: string) {
    const url = `${environment.mkgoURL}/api/v1/stockMove/rebuild-order-os-available/${partId}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    return firstValueFrom(this.http.post(url, {}, header).pipe(first()));
  }

  async update(movement: ApiMovementPost) {
    const url = `${environment.mkgoURL}/api/v1/stockMove/item/${movement.id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp = await this.http.put(url, movement, header).pipe(first()).toPromise();
    return resp;
  }

  async remove(id: string): Promise<any> {
    const url = `${environment.mkgoURL}/api/v1/stockMove/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp = await this.http.delete(url, header).pipe(first()).toPromise()
    return resp
  }


  /**
   * @param inOut true: in, false: out
   */
  public getMoveType(moveStock: boolean, inOut: boolean, isCancelling: boolean): MoveTypes {
    if (inOut) {
      if (isCancelling) {
        return moveStock ? MoveTypes["moveStock-in-cancelled"] : MoveTypes["noMoveStock-in-cancelled"];
      } else {
        return moveStock ? MoveTypes["moveStock-in"] : MoveTypes["noMoveStock-in"];
      }
    } else {
      if (isCancelling) {
        return moveStock ? MoveTypes["moveStock-out-cancelled"] : MoveTypes["noMoveStock-out-cancelled"]
      } else {
        return moveStock ? MoveTypes["moveStock-out"] : MoveTypes["noMoveStock-out"]
      }
    }
  }

  private _toFiniteNumber(value: unknown): number | undefined {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  private _appendCurrentPartCostFields(movement: ApiMovementPost, part?: Part): void {
    const currentPartCost = this._toFiniteNumber(part?.cost);
    const currentPartAverageCost = this._toFiniteNumber(part?.averageCost);

    if (currentPartCost !== undefined) {
      movement.currentPartCost = currentPartCost;
    }

    if (currentPartAverageCost !== undefined) {
      movement.currentPartAverageCost = currentPartAverageCost;
    }
  }

  private _appendSourceItemCostFields(movement: ApiMovementPost, sourceItem: {
    purchaseValue?: unknown;
    purchaseAverage?: unknown;
  }): void {
    const sourceItemCost = this._toFiniteNumber(sourceItem.purchaseValue);
    const sourceItemAverageCost = this._toFiniteNumber(sourceItem.purchaseAverage);

    if (sourceItemCost !== undefined) {
      movement.sourceItemCost = sourceItemCost;
    }

    if (sourceItemAverageCost !== undefined) {
      movement.sourceItemAverageCost = sourceItemAverageCost;
    }
  }

  /**
   * Re-read the part so `currentPartCost` / `currentPartAverageCost` are the values in force
   * at the moment the move is registered.
   *
   * Multi-branch companies always refresh: the cost that matters is the branch's own
   * `BranchPart` cost, and the `Part` object carried by the caller may be a stale copy taken
   * before `adjustBranchPartStock()` ran. Single-company installs only refresh when the
   * caller's snapshot has no usable cost.
   */
  private async _resolvePartForCurrentCostSnapshot(part?: Part, fallbackPartId?: string): Promise<Part | undefined> {
    const targetPartId = part?.id || fallbackPartId;

    if (!targetPartId) {
      return part;
    }

    const shouldRefreshPartFromApi =
      this.dataService.isMultiBranch()
      || !Number.isFinite(Number(part?.cost))
      || !Number.isFinite(Number(part?.averageCost));

    if (!shouldRefreshPartFromApi) {
      return part;
    }

    try {
      return await this._partService.get(targetPartId);
    } catch (error) {
      console.error(error);
      return part;
    }
  }

  async createMovementFromOS(data: {
    nfInvoice?: NF_response;
    OS: MkgOS;
    roPart: MkgOSPart;
    part: Part;
    moveType: MoveTypes;
    operationFunction: OperationFunction;
    diference: number;
    availableStockBeforeUpdate: number;
    balanceBeforeUpdate: number;
    osStockBeforeUpdate: number;
    orderStockBeforeUpdate: number;
    error?: IStockMoveError,
  }, movementCaller: string) {
    try {
      const { 
        OS,
        roPart,
        nfInvoice,
        moveType,
        operationFunction,
        part,
        diference,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        orderStockBeforeUpdate,
        osStockBeforeUpdate,
        error,
      } = data;

      let apiMovementToPost: ApiMovementPost = {
        item: part?.id ?? (typeof roPart.part === "string" ? roPart.part : roPart.part.id),
        amount: diference < 0 ? (diference * -1) : diference,
        balance: part?.stock,
        caller: `${movementCaller}; MovementService.createMovementFromOS()`,
        Client: OS.client.id || OS.clientTmp,
        currentAvailableStock: part?.availableStock,
        operationId: OS.operationId,
        functionType: operationFunction,
        moveType,
        OS: OS.id,
        userId: this.dataService.user.id,
        unitaryValue: isSignal(roPart.saleValue) ? roPart.saleValue() : roPart.saleValue,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        orderStockBeforeUpdate,
        osStockBeforeUpdate,
        osStockAfterUpdate: part?.osStock,
        orderStockAfterUpdate: part?.orderStock,
        error,
      }
  
      if(OS.client){
        apiMovementToPost.Client = OS.client.id;
      }
  
      if (nfInvoice) {
        apiMovementToPost.note = nfInvoice.id;
      }

      const partForCostSnapshot = await this._resolvePartForCurrentCostSnapshot(
        part,
        typeof roPart.part === 'string' ? roPart.part : roPart.part?.id,
      );

      this._appendCurrentPartCostFields(apiMovementToPost, partForCostSnapshot);
      this._appendSourceItemCostFields(apiMovementToPost, {
        purchaseValue: roPart.purchaseValue,
        purchaseAverage: roPart.purchaseAverage,
      });
      
      await this.register(apiMovementToPost);
    } catch (error) {
      console.error(error);
      throw error;
    } 
  }

  async createMovementFromOrder(movementData: {
    order: OrderFull | IOrder;
    orderItem: OrderItem | IOrderItem;
    moveType: MoveTypes;
    operationFunction: OperationFunction;
    caller: string;
    operationId: string;
    diference: number;
    availableStockBeforeUpdate: number;
    balanceBeforeUpdate: number;
    osStockBeforeUpdate: number;
    orderStockBeforeUpdate: number;
    error?: IStockMoveError,
  }) {
    const { 
      moveType,
      operationId,
      operationFunction,
      order,
      orderItem,
      caller,
      diference,
      availableStockBeforeUpdate,
      balanceBeforeUpdate,
      orderStockBeforeUpdate,
      osStockBeforeUpdate,
      error,
    } = movementData;

    const part = await this._partService.get(orderItem.part);
    
    let movement: ApiMovementPost = {
      functionType: operationFunction,
      amount: diference < 0 ? (diference * -1) : diference,
      item: orderItem.part,
      unitaryValue: orderItem.saleValue,
      moveType: moveType,
      balance: part.stock,
      userId: this.dataService.user.id,
      orderId: order._id,
      operationId,
      caller: caller + "; MovementService.createMovementFromOrder()",
      currentAvailableStock: part.availableStock,
      Client: order.client,
      availableStockBeforeUpdate,
      balanceBeforeUpdate,
      orderStockBeforeUpdate,
      osStockBeforeUpdate,
      osStockAfterUpdate: part.osStock,
      orderStockAfterUpdate: part.orderStock,
      error
    }

    const partForCostSnapshot = await this._resolvePartForCurrentCostSnapshot(part, orderItem.part);

    this._appendCurrentPartCostFields(movement, partForCostSnapshot);
    this._appendSourceItemCostFields(movement, {
      purchaseValue: orderItem.purchaseValue,
      purchaseAverage: orderItem.purchaseAverage,
    });

    await this.register(movement);
  }
  
  async createMovementFromInvoice(data: {
    item: InvoiceItem;
    operation: Operation;
    invoice: Pick<NF_response | IssueOsInvoiceResponse["partInvoice"] | IssueOrderInvoiceResponse["partInvoice"], "numero" | "id" | "serie">;
    senderID: string;
    moveType: MoveTypes;
    part: Part;
    amount?: number;
    orderId?: string;
    OSId?: string;
    caller: string;
    operationFuntion: OperationFunction;
    isSupplier: boolean;
    availableStockBeforeUpdate: number;
    balanceBeforeUpdate: number;
    osStockBeforeUpdate: number;
    orderStockBeforeUpdate: number;
    fromXmlImport: boolean
    companyPartsGetCnpj?: string;
    error?: IStockMoveError;
    amountFromImportedInvoice?: number;
    measurementUnitFromImportedInvoice?: string;
    orderItem?: IOrderItem | OrderItem;
    roPart?: MkgOSPart;
  }) {
    const { 
      invoice,
      item,
      operation,
      senderID,
      moveType,
      part,
      orderId,
      caller,
      amount,
      operationFuntion,
      OSId,
      isSupplier,
      availableStockBeforeUpdate,
      balanceBeforeUpdate,
      orderStockBeforeUpdate,
      osStockBeforeUpdate,
      error,
      amountFromImportedInvoice,
      fromXmlImport,
      measurementUnitFromImportedInvoice,
      orderItem,
      roPart,
      companyPartsGetCnpj
    } = data;

    let movement: ApiMovementPost = {
      functionType: operationFuntion,
      serial: invoice.serie,
      note: invoice?.id,
      amount: amount ?? item.amount,
      invoiceNumber: invoice?.numero,
      item: part.id,
      unitaryValue: item.value,
      moveType: moveType,
      balance: part.stock,
      userId: this.dataService.user.id,
      operationId: operation.id,
      caller: caller + "; MovementService.CreateMovementFromInvoice()",
      currentAvailableStock: part.availableStock,
      availableStockBeforeUpdate,
      balanceBeforeUpdate,
      osStockAfterUpdate: part.osStock,
      osStockBeforeUpdate,
      orderStockAfterUpdate: part.orderStock,
      orderStockBeforeUpdate,
      error,
      fromXmlImport,
    }

    if (fromXmlImport) {
      movement.amountFromImportedInvoice = amountFromImportedInvoice;
      movement.measurementUnitFromImportedInvoice = measurementUnitFromImportedInvoice;
    }

    if(senderID){
      if (isSupplier) {
        movement.Supplier = senderID;
      } else {
        movement.Client = senderID;
      }
    }

    if (orderId) {
      movement.orderId = orderId;

      this._appendSourceItemCostFields(movement, {
        purchaseValue: orderItem?.purchaseValue,
        purchaseAverage: orderItem?.purchaseAverage,
      });
    }

    if (OSId) {
      movement.OS = OSId;

      this._appendSourceItemCostFields(movement, {
        purchaseValue: roPart?.purchaseValue,
        purchaseAverage: roPart?.purchaseAverage,
      });
    }

    const partForCostSnapshot = await this._resolvePartForCurrentCostSnapshot(part, part?.id);

    this._appendCurrentPartCostFields(movement, partForCostSnapshot);
    
    await this.register(movement, companyPartsGetCnpj)
  }

  async createMovementFromNF(data: { 
    part: Part;
    amount: number;
    value: number;
    operation: Operation;
    invoice: NF_response;
    senderID: string;
    caller: string;
    OS?: MkgOS;
    availableStockBeforeUpdate: number;
    balanceBeforeUpdate: number;
    osStockBeforeUpdate: number;
    orderStockBeforeUpdate: number;
    roPart?: MkgOSPart;
    error?: IStockMoveError;
  }) {
    const { 
      part,
      amount,
      value,
      operation,
      invoice,
      senderID,
      caller,
      OS,
      availableStockBeforeUpdate,
      balanceBeforeUpdate,
      orderStockBeforeUpdate,
      osStockBeforeUpdate,
      roPart,
      error,
    } = data;

    let movement: ApiMovementPost = {
      description: operation.description,
      functionType: operation.operationFunction,
      serial: invoice?.serie,
      note: invoice?.id,
      invoiceNumber: invoice?.numero,
      amount,
      item: part.id,
      unitaryValue: value,
      moveType: this.getMoveType(operation.changeStock, operation.entry, false),
      balance: part.stock,
      userId: this.dataService.user.id,
      currentAvailableStock: part.availableStock,
      operationId: operation.id,
      caller: caller + "; MovementService.CreateMovementFromNF()",
      availableStockBeforeUpdate,
      balanceBeforeUpdate,
      osStockAfterUpdate: part.osStock,
      osStockBeforeUpdate,
      orderStockAfterUpdate: part.orderStock,
      orderStockBeforeUpdate,
      error
    }

    if (senderID) {
      movement.Client = senderID;
    }

    if (OS) {
      movement.OS = OS.id;
    }

    const partForCostSnapshot = await this._resolvePartForCurrentCostSnapshot(part, part?.id);

    this._appendCurrentPartCostFields(movement, partForCostSnapshot);

    if (roPart) {
      this._appendSourceItemCostFields(movement, {
        purchaseValue: roPart.purchaseValue,
        purchaseAverage: roPart.purchaseAverage,
      });
    }

    await this.register(movement);
  }

  async createMovementFromThePartEditingComponent(data: {
    part: Part;
    newBalance: number;
    newOsStock: number;
    newOrderStock: number;
    newAvailableStock: number;
    caller: string;
    partAtTheComponentStart: Part;
  }) {
    const { newAvailableStock, newBalance, newOsStock, newOrderStock, part, caller, partAtTheComponentStart } = data;
    const availableStockBeforeUpdate = partAtTheComponentStart.availableStock,
          balanceBeforeUpdate = partAtTheComponentStart.stock,
          orderStockBeforeUpdate = partAtTheComponentStart.orderStock,
          osStockBeforeUpdate = partAtTheComponentStart.osStock;

    let movement: ApiMovementPost = {
      description: JSON.stringify(this._createDescriptionForThePartEditingCompoent(part, partAtTheComponentStart, { 
        newAvailableStock, 
        newBalance, 
        newOsStock, 
        newOrderStock
       })),
      amount: 0,
      availableStockBeforeUpdate,
      balance: newBalance,
      balanceBeforeUpdate,
      caller,
      currentAvailableStock: newAvailableStock,
      functionType: "Estoque alterado manualmente pela edição da peça",
      osStockAfterUpdate: newOsStock,
      osStockBeforeUpdate,
      item: part.id,
      moveType: MoveTypes.itemManuallyChangedByStockEdition,
      operationId: null,
      orderStockAfterUpdate: newOrderStock,
      orderStockBeforeUpdate,
      unitaryValue: part.saleValue,
      userId: this.dataService.user.id ?? this.dataService.user._id,
      
    }

    const partForCostSnapshot = await this._resolvePartForCurrentCostSnapshot(part, part?.id);

    this._appendCurrentPartCostFields(movement, partForCostSnapshot);
    
    await this.register(movement);
  }
  
  private _createDescriptionForThePartEditingCompoent(part: Part, partAtTheComponentStart: Part, updates: {
    newBalance: number;
    newOsStock: number;
    newOrderStock: number;
    newAvailableStock: number; 
  }) {
    return {
      partAtTheComponentStartStockValues: {
        stock: partAtTheComponentStart.stock,
        osStockBeforeUpdate: partAtTheComponentStart.osStock,
        orderStockBeforeUpdate: partAtTheComponentStart.orderStock,
        availableStock: partAtTheComponentStart.availableStock
      },
      partStockValuesFromGetBeforeUpdate: {
        stock: part.stock,
        osStockBeforeUpdate: part.osStock,
        orderStockBeforeUpdate: part.orderStock,
        availableStock: part.availableStock
      },
      updatedValues: {
        stock: updates.newBalance,
        osStockAfterUpdate: updates.newOsStock,
        orderStockAfterUpdate: updates.newOrderStock,
        availableStock: updates.newAvailableStock,
      }
    };
  }
  
  getPaginated(queryParams: ApiPaginatedMovementQueryParams): Observable<ApiPaginatedResponse<MovementGet>> {
    const url = `${environment.mkgoURL}/api/v1/stockMove/page`;
    let params = PaginationService.getParams(queryParams);

    return this.dataService.httpOptions(this._isFromMatrix, params).pipe(
      switchMap(headers => this.http.get<ApiPaginatedResponse<MovementGet>>(url, headers)),
      first(),
      catchError(err => {
        console.error(err)
        if (err.error.error === API_ERRORS.notFound) {
          return of(PaginationService.notFoundErrorDataToReturn());
        } else {
          throw err;
        }
      }),
      switchMap(resp => {
        if (queryParams.all === 1 && resp.hasNextPage) {
          queryParams.limit = resp.totalDocs;
          params = PaginationService.getParams(queryParams);
          return this.dataService.httpOptions(this._isFromMatrix, params).pipe(
            switchMap(headers2 => this.http.get<ApiPaginatedResponse<MovementGet>>(url, headers2)),
            catchError(err => {
              console.error(err)
              if (err.error.error === API_ERRORS.notFound) {
                return of(PaginationService.notFoundErrorDataToReturn());
              } else {
                throw err;
              }
            }),
            first()
          )
        } else {
          return of(resp);
        }
      })
    );
  }
}
